import { isMoonPaySupportedFiat } from '../constants/moonPaySupportedFiats';
import { SessionData } from '../services/embedService';
import { exchangeRateService } from '../services/exchangeRateService';

/** Maps backend crypto codes (e.g. USDT_POLYGON) to MoonPay defaultCurrencyCode. */
export function moonPayCryptoCode(cryptoCurrency: string): string {
  const raw = cryptoCurrency.trim().toLowerCase();
  if (!raw) return 'usdc_polygon';

  const token = raw.split('_')[0] || 'usdc';
  const network =
    raw.includes('polygon') || raw.includes('matic')
      ? 'polygon'
      : raw.includes('arbitrum')
        ? 'arbitrum'
        : raw.includes('optimism')
          ? 'optimism'
          : raw.includes('base')
            ? 'base'
            : raw.includes('solana') || raw.endsWith('_sol')
              ? 'solana'
              : raw.includes('ethereum') || raw === 'eth'
                ? 'ethereum'
                : 'polygon';

  if (token === 'usdc') {
    if (network === 'polygon') return 'usdc_polygon';
    if (network === 'arbitrum') return 'usdc_arbitrum';
    if (network === 'optimism') return 'usdc_optimism';
    if (network === 'base') return 'usdc_base';
    if (network === 'solana') return 'usdc_sol';
    return 'usdc';
  }

  if (token === 'usdt') {
    if (network === 'polygon') return 'usdt_polygon';
    if (network === 'arbitrum') return 'usdt_arbitrum';
    if (network === 'optimism') return 'usdt_optimism';
    if (network === 'solana') return 'usdt_sol';
    return 'usdt';
  }

  if (token === 'eth') {
    if (network === 'polygon') return 'eth_polygon';
    if (network === 'arbitrum') return 'eth_arbitrum';
    if (network === 'optimism') return 'eth_optimism';
    if (network === 'base') return 'eth_base';
    return 'eth';
  }

  if (token === 'btc' || token === 'matic') return token;

  return token.length <= 20 ? token : 'usdc_polygon';
}

function formatMoonPayFiatAmount(amount: number, fiatCode: string): string {
  const code = fiatCode.toLowerCase();
  const zeroDecimal = new Set(['jpy', 'krw', 'vnd', 'idr']);
  if (zeroDecimal.has(code)) {
    return String(Math.max(1, Math.round(amount)));
  }
  return String(Math.round(amount * 100) / 100);
}

/**
 * Build MoonPay widget params from checkout session.
 * Unsupported fiat (e.g. AFN) is converted to USD so the sandbox widget can open.
 */
export async function buildMoonPayParamsFromSession(
  session: SessionData
): Promise<Record<string, string>> {
  const sessionFiat = session.fiatCurrency.trim().toUpperCase() || 'USD';
  let baseCurrencyCode = sessionFiat.toLowerCase();
  let fiatAmount = session.fiatAmount > 0 ? session.fiatAmount : 0;

  if (!isMoonPaySupportedFiat(baseCurrencyCode)) {
    const rates = await exchangeRateService.getExchangeRates();
    const rate = rates[sessionFiat];
    if (rate && rate > 0 && fiatAmount > 0) {
      fiatAmount = Math.round((fiatAmount / rate) * 100) / 100;
    }
    baseCurrencyCode = 'usd';
  }

  const params: Record<string, string> = {
    baseCurrencyCode,
    defaultCurrencyCode: moonPayCryptoCode(session.cryptoCurrency),
  };

  if (fiatAmount > 0) {
    params.baseCurrencyAmount = formatMoonPayFiatAmount(fiatAmount, baseCurrencyCode);
  }

  return params;
}

/** @deprecated Use buildMoonPayParamsFromSession for async fiat fallback. */
export function moonPayParamsFromSession(session: SessionData): Record<string, string> {
  const fiat = session.fiatCurrency.trim().toLowerCase() || 'usd';
  const code = isMoonPaySupportedFiat(fiat) ? fiat : 'usd';
  const params: Record<string, string> = {
    baseCurrencyCode: code,
    defaultCurrencyCode: moonPayCryptoCode(session.cryptoCurrency),
  };
  if (session.fiatAmount > 0) {
    params.baseCurrencyAmount = formatMoonPayFiatAmount(session.fiatAmount, code);
  }
  return params;
}
